import React, { useEffect, useState } from 'react';
import { FileText, Loader2, Plus, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { TemplateBlockEditor, newId } from './TemplateBlockEditor';
import { leadMeetingsAPI } from '../lib/api';

export const MeetingTemplateDialog = () => {
  const [open, setOpen] = useState(false);
  const [tpl, setTpl] = useState(null);
  const [bloques, setBloques] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTpl(null);
    leadMeetingsAPI.getTemplate().then((t) => { setTpl(t); setBloques(t.bloques); }).catch((e) => toast.error(e.message));
  }, [open]);

  const update = (i, b) => setBloques((prev) => prev.map((x, j) => (j === i ? b : (b.proximos_pasos && !prev[i].proximos_pasos ? { ...x, proximos_pasos: false } : x))));
  const move = (i, d) => setBloques((prev) => { const bs = [...prev]; [bs[i], bs[i + d]] = [bs[i + d], bs[i]]; return bs; });
  const add = () => setBloques((prev) => [...prev, { id: newId(), titulo: 'Nuevo bloque', guion_html: '', campos: [], proximos_pasos: false }]);

  const save = async () => {
    if (bloques.length === 0) { toast.error('La plantilla necesita al menos un bloque'); return; }
    const clean = bloques.map((b) => ({ ...b, titulo: b.titulo.trim() || 'Sin título', campos: b.campos.filter((c) => c.label.trim()) }));
    setSaving(true);
    try {
      await leadMeetingsAPI.saveTemplate(clean);
      toast.success('Plantilla guardada. Se usará en las reuniones nuevas.');
      setOpen(false);
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="meeting-template-open-btn">
        <FileText className="w-4 h-4 mr-2" />Plantilla de reunión
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="meeting-template-dialog">
          <DialogHeader>
            <DialogTitle>Plantilla maestra de reunión comercial</DialogTitle>
            <DialogDescription>
              El guion se muestra fijo en cada reunión; los campos se rellenan en cada una. Los cambios solo afectan a las reuniones nuevas.
              {tpl?.updated_at && ` · Última edición: ${tpl.updated_by_name} el ${new Date(tpl.updated_at).toLocaleDateString('es-ES')}`}
            </DialogDescription>
          </DialogHeader>
          {tpl ? (
            <div className="space-y-4">
              {bloques.map((b, i) => (
                <TemplateBlockEditor key={b.id} bloque={b} index={i} total={bloques.length}
                  onChange={(nb) => update(i, nb)} onMove={(d) => move(i, d)} onRemove={() => setBloques((prev) => prev.filter((_, j) => j !== i))} />
              ))}
              <Button variant="outline" className="w-full border-dashed" onClick={add} data-testid="tpl-add-block-btn"><Plus className="w-4 h-4 mr-2" />Añadir bloque</Button>
            </div>
          ) : (
            <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
          )}
          <div className="flex justify-end gap-2 sticky -bottom-6 bg-background py-3 border-t">
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="meeting-template-cancel-btn">Cancelar</Button>
            <Button onClick={save} disabled={saving || !tpl} data-testid="meeting-template-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Guardar plantilla
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
